import {
 CreationAssetPage,CreationWorkPage,CreationResolveCommand,CreationResolution,CreationConflictEnvelope,
 LibraryAssetsQuery,LibraryWorksQuery,ResourceMetadata,ResourceMetadataUpdate,CreationMutationIntent,
 type ResourceAssetData,type ResourceWorkData,type ResourceMetadataData,type ResourceMetadataUpdateData,
} from '@zzy/contracts';
import {creationScope,type CreationSession} from './creation-session';

export type ResourceKind='assets'|'works';
export type ResourcePage<T>={status:'idle'|'loading'|'ready'|'error';items:T[];nextCursor:string|null;hasMore:boolean;message?:string};
export type MetadataIntent={operation:'library.metadata';scope:string;key:string;command:ResourceMetadataUpdateData};
export type MetadataConflict={command:ResourceMetadataUpdateData;current:ResourceMetadataData|null};
export type ResourceClientState={scope:string;assets:ResourcePage<ResourceAssetData>;works:ResourcePage<ResourceWorkData>;
 pending:MetadataIntent[];active:string[];conflicts:Record<string,MetadataConflict>;message:string;authRequired:boolean;csrfRequired:boolean;storageBlocked:boolean};
export type MutationStorage=Pick<Storage,'getItem'|'setItem'>;
type Query=Record<string,string|number|boolean|undefined>;
const empty=<T>():ResourcePage<T>=>({status:'idle',items:[],nextCursor:null,hasMore:false});
const owner=(session:CreationSession)=>JSON.stringify([session.siteId,session.accountId,session.source]);
export const metadataTarget=(value:{targetType:string;targetId:string})=>JSON.stringify([value.targetType,value.targetId]);
export class ResourceApiError extends Error {
 constructor(readonly status:number,readonly code:string,readonly current?:ResourceMetadataData|null){super(code)}
}
export function resourceMessage(error:unknown){
 if(error instanceof ResourceApiError){
  if(error.status===401)return '登录已失效，请重新登录后核对原操作。';
  if(error.code==='CSRF')return '登录状态需要刷新，请刷新后继续。';
  if(error.code==='REVISION_CONFLICT')return '内容已被其他操作修改，请先核对当前版本。';
  if(error.code==='INVALID_CURSOR')return '列表已失效，请重新读取。';
  if(error.status===403)return '当前账户无权执行此操作。';
  if(error.code==='RESOURCE_KIND_UNAVAILABLE')return '当前仅支持图片资源。';
  if(error.status===429)return '操作过于频繁，请稍后重试。';
  if(error.status===404)return '当前资源暂不可用，请重新读取。';
 }
 return '资源操作未能确认，请保留当前内容并重试读取或核对原操作。';
}

// A page and its writes share this controller. It owns no account/session polling.
export class CreationResourceClient {
 state:ResourceClientState;
 private session:CreationSession;
 private generation=0;
 private connected=false;
 private listeners=new Set<(state:ResourceClientState)=>void>();
 private reads:Partial<Record<ResourceKind,AbortController>>={};
 private tickets={assets:0,works:0};
 private filters:Partial<Record<ResourceKind,Query>>={};
 private writes=new Set<AbortController>();
 private metadataClock=0;
 private updates=new Map<string,{at:number;value:ResourceMetadataData}>();
 constructor(session:CreationSession,private readonly deps:{fetcher?:typeof fetch;storage:()=>MutationStorage;newKey?:()=>string}){
  this.session=session;this.state=this.initial(creationScope(session));
 }
 private initial(scope:string):ResourceClientState{return {scope,assets:empty(),works:empty(),pending:[],active:[],conflicts:{},message:'',authRequired:false,csrfRequired:false,storageBlocked:false}}
 subscribe(listener:(state:ResourceClientState)=>void){this.listeners.add(listener);return()=>{this.listeners.delete(listener)}}
 private publish(patch:Partial<ResourceClientState>){this.state={...this.state,...patch};for(const listener of this.listeners)listener(this.state)}
 connect(session:CreationSession){
  const scope=creationScope(session),changed=scope!==this.state.scope;
  if(changed||!this.connected){this.cancel();this.state=this.initial(scope);this.filters={};this.updates.clear();this.metadataClock=0;}
  this.session=session;this.connected=true;
  try{this.publish({pending:this.records(),authRequired:false,csrfRequired:false,storageBlocked:false})}
  catch{this.publish({storageBlocked:true,message:'无法安全保存待核对记录，本次修改不会发送。'})}
 }
 private cancel(){this.generation++;for(const read of Object.values(this.reads))read?.abort();for(const write of this.writes)write.abort();this.writes.clear();}
 dispose(){this.connected=false;this.cancel()}
 private live(generation:number){return this.connected&&this.generation===generation}
 private storageKey(){return 'zzy-resource-mutations:v1:'+owner(this.session)}
 private records():MetadataIntent[]{
  const raw=this.deps.storage().getItem(this.storageKey());if(raw===null)return [];
  const input:unknown=JSON.parse(raw);if(!Array.isArray(input)||input.length>50)throw Error('INVALID_PENDING');
  const seen=new Set<string>(),targets=new Set<string>();
  return input.map(value=>{
   if(!value||typeof value!=='object'||Object.keys(value).sort().join(',')!=='command,key,operation,scope'||value.operation!=='library.metadata'||typeof value.scope!=='string')throw Error('INVALID_PENDING');
   const scope:unknown=JSON.parse(value.scope);
   if(!Array.isArray(scope)||scope.length!==4||scope.some(part=>typeof part!=='string'||!part)||JSON.stringify(scope.slice(0,3))!==owner(this.session))throw Error('INVALID_PENDING_OWNER');
   const key=CreationMutationIntent.shape.key.parse(value.key),command=ResourceMetadataUpdate.parse(value.command),target=metadataTarget(command);
   if(seen.has(key)||targets.has(target))throw Error('DUPLICATE_PENDING');seen.add(key);targets.add(target);
   return {operation:'library.metadata',scope:value.scope,key,command};
  });
 }
 private store(records:MetadataIntent[]){
  if(records.length>50)throw Error('PENDING_LIMIT');
  const storage=this.deps.storage(),text=JSON.stringify(records);storage.setItem(this.storageKey(),text);
  if(storage.getItem(this.storageKey())!==text)throw Error('STORAGE_UNCONFIRMED');
  this.publish({pending:records});
 }
 private drop(key:string){this.store(this.records().filter(record=>record.key!==key))}
 private note(error:unknown){this.publish({message:resourceMessage(error),...(error instanceof ResourceApiError&&error.status===401?{authRequired:true}:{}),...(error instanceof ResourceApiError&&error.code==='CSRF'?{csrfRequired:true}:{})})}
 canWrite(){return this.connected&&!this.state.authRequired&&!this.state.csrfRequired&&!this.state.storageBlocked}
 private async request<T>(path:string,schema:{parse:(input:unknown)=>T},options:{signal:AbortSignal;method?:string;body?:unknown;key?:string}):Promise<T>{
  const response=await (this.deps.fetcher??fetch)(path,{method:options.method??'GET',credentials:'same-origin',cache:'no-store',signal:options.signal,
   ...(options.body===undefined?{}:{headers:{'Content-Type':'application/json','X-CSRF-Token':this.session.csrf,'Idempotency-Key':options.key!},body:JSON.stringify(options.body)})});
  let json:any;try{json=await response.json()}catch{throw new ResourceApiError(response.status,'INVALID_RESPONSE')}
  if(!response.ok){
   if(response.status===409&&json?.error?.code==='REVISION_CONFLICT'){
    const conflict=CreationConflictEnvelope.parse(json).error;
    const current=conflict.current===null?null:ResourceMetadata.parse(conflict.current);
    throw new ResourceApiError(409,conflict.code,current);
   }
   throw new ResourceApiError(response.status,typeof json?.error?.code==='string'?json.error.code:'REQUEST_FAILED');
  }
  return schema.parse(json?.data);
 }
 loadAssets(query:Query={}){return this.load('assets',query)}
 loadWorks(query:Query={}){return this.load('works',query)}
 private async load(kind:ResourceKind,input:Query,cursor?:string,recovered=false):Promise<void>{
  const parsed=(kind==='assets'?LibraryAssetsQuery:LibraryWorksQuery).parse({...input,cursor});
  const {cursor:_,...filters}=parsed;const query=filters as Query;
  const filterKey=(value:Query)=>JSON.stringify(Object.entries(value).filter(([,v])=>v!==undefined).sort(([a],[b])=>a.localeCompare(b)));
  const same=!!this.filters[kind]&&filterKey(this.filters[kind]!)===filterKey(query);
  this.filters[kind]=query;this.reads[kind]?.abort();const controller=new AbortController();this.reads[kind]=controller;
  const generation=this.generation,readAt=this.metadataClock,ticket=++this.tickets[kind],live=()=>this.live(generation)&&ticket===this.tickets[kind]&&!controller.signal.aborted;
  const previous=same?this.state[kind].items:[];
  this.publish({[kind]:{status:'loading',items:previous,nextCursor:null,hasMore:false}});
  const search=new URLSearchParams();for(const [key,value]of Object.entries(parsed))if(value!==undefined)search.set(key,String(value));
  try{
   const page=kind==='assets'?await this.request('/api/v1/library/assets?'+search,CreationAssetPage,{signal:controller.signal}):await this.request('/api/v1/library/works?'+search,CreationWorkPage,{signal:controller.signal});
   if(!live())return;
   const items=(cursor?[...previous,...page.items]:page.items).map(item=>{const update=this.updates.get(metadataTarget(item));return update&&update.at>readAt?{...item,...update.value}:item})
    .filter(item=>query.favorite===undefined||item.favorite===query.favorite);
   const unique=new Map(items.map(item=>[kind==='assets'?(item as ResourceAssetData).assetId:(item as ResourceWorkData).workId,item]));
   this.publish({[kind]:{status:'ready',items:[...unique.values()],nextCursor:page.nextCursor,hasMore:page.hasMore}});
  }catch(error){
   if(!live())return;
   if(cursor&&!recovered&&error instanceof ResourceApiError&&error.status===400&&error.code==='INVALID_CURSOR'){
    this.publish({[kind]:empty(),message:'列表已失效，已从首页重新读取。'});return this.load(kind,query,undefined,true);
   }
   this.note(error);this.publish({[kind]:{status:'error',items:previous,nextCursor:null,hasMore:false,message:resourceMessage(error)}});
  }
 }
 more(kind:ResourceKind){const page=this.state[kind];if(page.status==='loading'||!page.nextCursor)return Promise.resolve();return this.load(kind,this.filters[kind]??{},page.nextCursor)}
 reload(kind:ResourceKind){return this.load(kind,this.filters[kind]??{})}
 private apply(metadata:ResourceMetadataData){
  this.updates.set(metadataTarget(metadata),{at:++this.metadataClock,value:metadata});
  const kind=metadata.targetType==='asset'?'assets':'works',page=this.state[kind],filter=this.filters[kind];
  const items=page.items.map(item=>metadataTarget(item)===metadataTarget(metadata)?{...item,...metadata}:item)
   .filter(item=>filter?.favorite===undefined||item.favorite===filter.favorite);
  const conflicts={...this.state.conflicts};delete conflicts[metadataTarget(metadata)];
  this.publish({[kind]:{...page,items},conflicts,message:'修改已保存。'});
 }
 async updateMetadata(input:unknown){
  if(!this.canWrite())return;
  const command=ResourceMetadataUpdate.parse(input),target=metadataTarget(command);let record:MetadataIntent;
  try{
   const records=this.records();if(records.some(item=>metadataTarget(item.command)===target)){this.publish({pending:records,message:'请先核对该资源的原操作。'});return;}
   record={operation:'library.metadata',scope:this.state.scope,key:CreationMutationIntent.shape.key.parse((this.deps.newKey??(()=>crypto.randomUUID()))()),command};
   this.store([...records,record]);
  }catch{this.publish({storageBlocked:true,message:'无法安全保存待核对记录，本次修改不会发送。'});return;}
  const generation=this.generation,controller=new AbortController();this.writes.add(controller);this.publish({active:[...this.state.active,record.key],message:''});
  try{
   const result=await this.request('/api/v1/library/metadata',ResourceMetadata,{method:'PUT',body:command,key:record.key,signal:controller.signal});
   if(!this.live(generation))return;if(metadataTarget(result)!==target)throw Error('TARGET_MISMATCH');
   this.drop(record.key);this.apply(result);
  }catch(error){
   if(!this.live(generation))return;
   if(error instanceof ResourceApiError&&error.status>=400&&error.status<500){
    try{this.drop(record.key)}catch{this.publish({storageBlocked:true});}
    if(error.code==='REVISION_CONFLICT'&&(error.current===null||error.current&&metadataTarget(error.current)===target))this.publish({conflicts:{...this.state.conflicts,[target]:{command,current:error.current}}});
    this.note(error);
   }else this.publish({message:'操作结果尚未确认，请核对原操作，不要重复修改。'});
  }finally{this.writes.delete(controller);if(this.live(generation))this.publish({active:this.state.active.filter(key=>key!==record.key)})}
 }
 async resolve(key:string){
  if(!this.canWrite()||this.state.active.includes(key))return;
  let record:MetadataIntent;try{const found=this.records().find(item=>item.key===key);if(!found)return;record=found}catch{this.publish({storageBlocked:true});return;}
  const generation=this.generation,controller=new AbortController();this.writes.add(controller);this.publish({active:[...this.state.active,key]});
  try{
   const command=CreationResolveCommand.parse({operation:record.operation,command:record.command});
   const result=await this.request('/api/v1/creation/mutations/resolve',CreationResolution,{method:'POST',body:command,key,signal:controller.signal});
   if(!this.live(generation))return;
   if(result.state==='outcome_unknown'){this.publish({message:'原操作结果仍待确认，请稍后主动核对。'});return;}
   if(result.operation!=='library.metadata'||metadataTarget(result.value)!==metadataTarget(record.command))throw Error('RESOLUTION_MISMATCH');
   this.drop(key);this.apply(result.value);
  }catch(error){if(this.live(generation))this.note(error)}
  finally{this.writes.delete(controller);if(this.live(generation))this.publish({active:this.state.active.filter(value=>value!==key)})}
 }
 reapply(target:string){const conflict=this.state.conflicts[target];if(!conflict?.current)return Promise.resolve();return this.updateMetadata({...conflict.command,expectedRevision:conflict.current.metadataRevision})}
}
